import { useEffect, type PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCreatePostFlow } from '@wlb/shared/context/CreatePostFlowContext';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { ProfileCheckStatus } from '@wlb/shared/types';
import { ListStatus } from '../../components/ui/ListStatus';
import { createPostReturnPath } from './createPostRoute';

export const requiresMandatoryFirstPost = (
  profile: { isNewUser?: boolean } | null | undefined,
): boolean => profile?.isNewUser === true;

export const resolveProfileGateState = (
  status: ProfileCheckStatus,
): 'loading' | 'ready' | 'error' => {
  if (status === 'ready') return 'ready';
  if (status === 'empty' || status === 'error') return 'error';
  return 'loading';
};

/**
 * Keeps a newly onboarded member in the composer until their first post is
 * accepted. GetMyProfile is authoritative, so direct URLs and browser Back
 * cannot bypass the requirement.
 */
export function MandatoryFirstPostGate({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    profile,
    fetchProfile,
    profileCheckStatus,
    profileCheckError,
  } = useProfile();
  const { setIsCreateMyOwnPostFlow } = useCreatePostFlow();
  const gateState = resolveProfileGateState(profileCheckStatus);
  const mandatory = requiresMandatoryFirstPost(profile);

  useEffect(() => {
    setIsCreateMyOwnPostFlow(mandatory);
    return () => setIsCreateMyOwnPostFlow(false);
  }, [mandatory, setIsCreateMyOwnPostFlow]);

  if (gateState === 'loading') {
    return (
      <div className="app-loader" role="status">
        {t('common.loading')}
      </div>
    );
  }

  if (gateState === 'error') {
    return (
      <main className="app-loader">
        <ListStatus
          variant="error"
          title={t('home.newUserWelcome.profileCheckFailed')}
          message={
            profileCheckError ??
            t('home.newUserWelcome.profileCheckUnavailable')
          }
          onAction={() => {
            fetchProfile().catch(() => undefined);
          }}
          busy={profileCheckStatus === 'loading'}
        />
      </main>
    );
  }

  if (mandatory && location.pathname !== '/create') {
    return (
      <Navigate
        to="/create"
        replace
        state={{ returnTo: createPostReturnPath(location) }}
      />
    );
  }

  return children;
}
